import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function SeattleToIreland() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar scrolled={scrolled} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f083d4dd01d867e80a1695051bdac3c1.jpg"
            alt="Seattle to Dublin relocation"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>

        <div className="relative max-w-5xl mx-auto text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Moving to Ireland from Seattle</h1>
          <p className="text-xl md:text-2xl mb-8 text-white/90">From the Pacific Northwest to the Emerald Isle: Your complete relocation guide</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/tools/cost-calculator"
              className="px-8 py-4 bg-[#D4AF37] hover:bg-[#C19B2F] text-white rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Compare Costs
            </Link>
            <Link
              to="/directory"
              className="px-8 py-4 bg-white hover:bg-gray-100 text-[#0A381F] rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Find Services
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-6 bg-gradient-to-br from-[#0A381F] to-[#0D4A28] rounded-2xl text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-flight-takeoff-line text-3xl"></i>
              </div>
              <div className="text-3xl font-bold mb-2">9-10 hours</div>
              <div className="text-white/80">Direct flight time</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-[#D4AF37] to-[#C19B2F] rounded-2xl text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-time-line text-3xl"></i>
              </div>
              <div className="text-3xl font-bold mb-2">+8 hours</div>
              <div className="text-white/80">Time difference</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-cloud-line text-3xl"></i>
              </div>
              <div className="text-3xl font-bold mb-2">Similar</div>
              <div className="text-white/80">Rainy climates</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-green-600 to-green-700 rounded-2xl text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-building-line text-3xl"></i>
              </div>
              <div className="text-3xl font-bold mb-2">Amazon</div>
              <div className="text-white/80">Has Dublin office</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Seattleites Love Ireland */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-4 text-center">Why Seattle Tech Workers Choose Ireland</h2>
          <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            Ireland is becoming the top European destination for Seattle's tech professionals
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mb-6">
                <i className="ri-cloud-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-[#0A381F] mb-4">Similar Weather</h3>
              <p className="text-gray-600">
                If you love Seattle's rainy, mild climate, you'll feel right at home in Ireland. No extreme temperatures, lots of green landscapes, and yes - plenty of rain!
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 flex items-center justify-center bg-blue-100 rounded-full mb-6">
                <i className="ri-code-s-slash-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-[#0A381F] mb-4">Tech Hub</h3>
              <p className="text-gray-600">
                Dublin is Europe's Silicon Valley. Amazon, Microsoft, Google, Meta - all the Seattle tech giants have major European headquarters in Ireland.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 flex items-center justify-center bg-purple-100 rounded-full mb-6">
                <i className="ri-cup-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-[#0A381F] mb-4">Coffee Culture</h3>
              <p className="text-gray-600">
                Ireland's specialty coffee scene rivals Seattle's. Independent roasters, third-wave cafes, and a thriving barista culture - you won't miss your daily brew.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-4 text-center">Seattle vs Dublin: Cost Comparison</h2>
          <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            How your Seattle expenses translate to Dublin living costs
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Housing Costs */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-[#0A381F] mb-6">Housing Costs</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">1-Bedroom Apartment (City Center)</div>
                    <div className="text-sm text-gray-500">Capitol Hill vs Dublin City Centre</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$2,400</div>
                    <div className="text-sm text-green-600">vs $1,800</div>
                  </div>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">2-Bedroom Apartment (City Center)</div>
                    <div className="text-sm text-gray-500">Downtown Seattle vs Dublin 2</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$3,500</div>
                    <div className="text-sm text-green-600">vs $2,600</div>
                  </div>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">3-Bedroom House (Suburbs)</div>
                    <div className="text-sm text-gray-500">Bellevue vs Malahide</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$3,200</div>
                    <div className="text-sm text-green-600">vs $2,400</div>
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-2 text-green-700 font-semibold">
                    <i className="ri-arrow-down-line"></i>
                    <span>Save $600-900/month on housing in Dublin</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tech Salaries */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-[#0A381F] mb-6">Tech Salaries</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">Software Engineer</div>
                    <div className="text-sm text-gray-500">Mid-level, 3-5 years experience</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$140,000</div>
                    <div className="text-sm text-orange-600">vs $95,000</div>
                  </div>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">Senior Engineer</div>
                    <div className="text-sm text-gray-500">6-10 years experience</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$180,000</div>
                    <div className="text-sm text-orange-600">vs $120,000</div>
                  </div>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">Product Manager</div>
                    <div className="text-sm text-gray-500">Mid-level</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$130,000</div>
                    <div className="text-sm text-orange-600">vs $85,000</div>
                  </div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-2 text-blue-700 font-semibold">
                    <i className="ri-information-line"></i>
                    <span>Lower salary offset by lower costs + better benefits</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-2xl font-bold text-[#0A381F] mb-6">Monthly Budget Comparison</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 font-semibold text-gray-900">Expense</th>
                    <th className="text-right py-4 px-4 font-semibold text-gray-900">Seattle</th>
                    <th className="text-right py-4 px-4 font-semibold text-gray-900">Dublin</th>
                    <th className="text-right py-4 px-4 font-semibold text-gray-900">Savings</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-4 px-4">Rent (1-bed, city center)</td>
                    <td className="text-right py-4 px-4">$2,400</td>
                    <td className="text-right py-4 px-4">$1,800</td>
                    <td className="text-right py-4 px-4 text-green-600 font-semibold">$600</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4">Groceries</td>
                    <td className="text-right py-4 px-4">$450</td>
                    <td className="text-right py-4 px-4">$450</td>
                    <td className="text-right py-4 px-4 text-gray-500">$0</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4">Transportation</td>
                    <td className="text-right py-4 px-4">$200</td>
                    <td className="text-right py-4 px-4">$140</td>
                    <td className="text-right py-4 px-4 text-green-600 font-semibold">$60</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4">Healthcare</td>
                    <td className="text-right py-4 px-4">$400</td>
                    <td className="text-right py-4 px-4">$100</td>
                    <td className="text-right py-4 px-4 text-green-600 font-semibold">$300</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4">Utilities + Internet</td>
                    <td className="text-right py-4 px-4">$180</td>
                    <td className="text-right py-4 px-4">$150</td>
                    <td className="text-right py-4 px-4 text-green-600 font-semibold">$30</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4">Dining Out</td>
                    <td className="text-right py-4 px-4">$400</td>
                    <td className="text-right py-4 px-4">$350</td>
                    <td className="text-right py-4 px-4 text-green-600 font-semibold">$50</td>
                  </tr>
                  <tr className="bg-gray-50 font-bold">
                    <td className="py-4 px-4">Total Monthly</td>
                    <td className="text-right py-4 px-4">$4,030</td>
                    <td className="text-right py-4 px-4">$2,990</td>
                    <td className="text-right py-4 px-4 text-green-600">$1,040</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-6 p-4 bg-green-50 rounded-lg">
              <p className="text-green-700 font-semibold">💰 Save over $12,000 per year on living expenses in Dublin!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Seattle Companies in Ireland */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-4 text-center">Seattle Tech Giants in Ireland</h2>
          <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            Transfer opportunities and job prospects for Seattle tech workers
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-[#0A381F] to-[#0D4A28] rounded-2xl p-8 text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-amazon-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Amazon</h3>
              <p className="text-white/90 mb-4">
                Amazon's European headquarters is in Dublin with over 2,500 employees. Internal transfers available for Seattle employees. AWS, Alexa, and retail teams all hiring.
              </p>
              <ul className="space-y-2 text-white/80">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#D4AF37]"></i>
                  <span>Internal transfer program</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#D4AF37]"></i>
                  <span>Relocation package offered</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#D4AF37]"></i>
                  <span>Visa sponsorship available</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-[#D4AF37] to-[#C19B2F] rounded-2xl p-8 text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-microsoft-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Microsoft</h3>
              <p className="text-white/90 mb-4">
                Microsoft's EMEA operations center in Dublin. Azure, Office 365, and Xbox teams. Strong preference for internal transfers from Redmond campus.
              </p>
              <ul className="space-y-2 text-white/80">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-white"></i>
                  <span>Redmond to Dublin transfers common</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-white"></i>
                  <span>Competitive relocation benefits</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-white"></i>
                  <span>Similar company culture</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-google-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Google</h3>
              <p className="text-white/90 mb-4">
                Google's European HQ in Dublin with 8,000+ employees. Search, Ads, Cloud, and YouTube teams. Active hiring for experienced engineers.
              </p>
              <ul className="space-y-2 text-white/80">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-white"></i>
                  <span>Largest tech employer in Ireland</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-white"></i>
                  <span>Seattle-level compensation</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-white"></i>
                  <span>Amazing campus and perks</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl p-8 text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-meta-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Meta (Facebook)</h3>
              <p className="text-white/90 mb-4">
                Meta's international headquarters in Dublin. Instagram, WhatsApp, and Oculus teams. Strong engineering culture similar to Seattle office.
              </p>
              <ul className="space-y-2 text-white/80">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-white"></i>
                  <span>3,000+ employees in Dublin</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-white"></i>
                  <span>Competitive tech salaries</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-white"></i>
                  <span>Internal mobility encouraged</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Lifestyle Comparison */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-4 text-center">Lifestyle: Seattle vs Ireland</h2>
          <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            What Seattleites love (and miss) about Irish life
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* What You'll Love */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full">
                  <i className="ri-heart-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#0A381F]">What You'll Love</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Similar Climate:</strong>
                    <p className="text-gray-600">Mild, rainy weather year-round - you'll feel right at home</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Outdoor Culture:</strong>
                    <p className="text-gray-600">Hiking, coastal walks, and green spaces everywhere</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Craft Beer Scene:</strong>
                    <p className="text-gray-600">Ireland's craft brewery boom rivals Seattle's beer culture</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">European Travel:</strong>
                    <p className="text-gray-600">Weekend trips to Paris, Barcelona, Amsterdam for &lt; $100</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Work-Life Balance:</strong>
                    <p className="text-gray-600">25+ vacation days, shorter hours, less grind culture</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">No Sales Tax:</strong>
                    <p className="text-gray-600">VAT included in prices - what you see is what you pay</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* What You'll Miss */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-orange-100 rounded-full">
                  <i className="ri-emotion-sad-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#0A381F]">What You'll Miss</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Mountains:</strong>
                    <p className="text-gray-600">No Cascades or Rainier - Ireland is flat by comparison</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Asian Food:</strong>
                    <p className="text-gray-600">Seattle's amazing Asian cuisine doesn't exist in Ireland</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Seahawks Games:</strong>
                    <p className="text-gray-600">Watching NFL means 6pm kickoffs become 2am wake-ups</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Pike Place Market:</strong>
                    <p className="text-gray-600">No equivalent to Seattle's iconic farmers market</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Tech Salaries:</strong>
                    <p className="text-gray-600">30-40% lower than Seattle (but costs are lower too)</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Amazon Prime Same-Day:</strong>
                    <p className="text-gray-600">Delivery is slower and more expensive in Ireland</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Practical Tips */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-12 text-center">Practical Tips for Seattleites</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-white">
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-flight-takeoff-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Direct Flights</h3>
              <p className="text-white/90 mb-4">Aer Lingus flies direct Seattle (SEA) to Dublin (9-10 hours). Book 2-3 months ahead for best prices ($500-900 round trip).</p>
              <p className="text-sm text-white/80">Tip: Overnight flights arrive Dublin morning - perfect timing</p>
            </div>

            <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl p-8 text-white">
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-umbrella-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Weather Prep</h3>
              <p className="text-white/90 mb-4">Bring your Seattle rain gear - it works perfectly in Ireland. Invest in good waterproof jacket and boots.</p>
              <p className="text-sm text-white/80">Tip: Ireland is milder - no need for heavy winter coats</p>
            </div>

            <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-8 text-white">
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-bank-card-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Banking</h3>
              <p className="text-white/90 mb-4">Open Revolut or Wise before leaving Seattle. Avoid Bank of America/Chase international fees.</p>
              <p className="text-sm text-white/80">Tip: Keep your US account for credit history and 401k</p>
            </div>
          </div>
        </div>
      </section>

      {/* Relocation Expert CTA */}
      <section className="py-16 px-6 bg-gradient-to-br from-[#0A381F] to-[#0D4A28]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
            <i className="ri-customer-service-2-line text-4xl"></i>
          </div>
          <h2 className="text-4xl font-bold mb-6">Questions About Moving from Seattle?</h2>
          <p className="text-xl text-white/90 mb-8">
            Our Ireland Relocation Expert can answer all your questions about visas, housing, tech jobs, and life in Ireland
          </p>
          <button
            onClick={() => {
              const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
              if (widget) widget.click();
            }}
            className="px-8 py-4 bg-[#D4AF37] hover:bg-[#C19B2F] text-white rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap inline-flex items-center gap-3"
          >
            <i className="ri-chat-3-line text-xl"></i>
            <span>Talk to Relocation Expert</span>
          </button>
        </div>
      </section>

      {/* Next Steps */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-6">Ready to Make the Move?</h2>
          <p className="text-xl text-gray-600 mb-12">
            Start your relocation journey with our comprehensive tools and resources
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/tools/visa-quiz"
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all cursor-pointer border border-gray-100"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-[#0A381F] rounded-full mx-auto mb-4">
                <i className="ri-questionnaire-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-[#0A381F] mb-2">Check Visa Eligibility</h3>
              <p className="text-gray-600 text-sm">Find out which visa you qualify for</p>
            </Link>

            <Link
              to="/tools/cost-calculator"
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all cursor-pointer border border-gray-100"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-[#D4AF37] rounded-full mx-auto mb-4">
                <i className="ri-calculator-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-[#0A381F] mb-2">Calculate Your Budget</h3>
              <p className="text-gray-600 text-sm">Compare Seattle vs Dublin costs</p>
            </Link>

            <Link
              to="/directory"
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all cursor-pointer border border-gray-100"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-purple-600 rounded-full mx-auto mb-4">
                <i className="ri-building-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-[#0A381F] mb-2">Find Services</h3>
              <p className="text-gray-600 text-sm">Immigration lawyers, movers, and more</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
